import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, Renderer2, SimpleChanges } from '@angular/core';
import { PaymentsService } from '../../services/payments.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Bulk customer remittance importer, embedded in the Customer Payment Entry screen.
 * Paste / upload a remittance (text, CSV, or PDF) for the already-selected customer,
 * the server parses + deterministically matches its lines to open invoices, the operator
 * reviews, then posts ONE receipt allocated across the matched invoices.
 */
@Component({
	selector: 'app-remittance-import',
	templateUrl: './remittance-import.component.html',
	styleUrls: ['./remittance-import.component.scss']
})
export class RemittanceImportComponent implements OnInit, OnChanges, OnDestroy {
	@Input() customer: any = null;               // the open customer (has debtorno, branchcode)
	@Input() bankaccounts: any[] = [];
	@Input() payment_methods: any[] = [];
	@Input() defaultBank: any = '';
	@Output() posted = new EventEmitter<any>();

	enabled = false;                              // config-gated: only clients with remittanceImport:true
	open = false;                                 // this component owns its own show/hide
	expanded = false;                             // focus/maximize mode (fullscreen overlay)
	filterStatus: string | null = null;          // click a summary chip to filter the table
	mode: 'input' | 'review' | 'done' = 'input';
	pasteText = '';
	fileName = '';
	fileBase64 = '';
	fileKind: 'pdf' | 'csv' | '' = '';

	parsing = false;
	posting = false;
	batch: any = null;
	result: any = null;

	// receipt header
	header = {
		bankaccount: '',
		payment_type: '',
		datepaid: new Date(),
		reference: 'ACH',
		notes: '',
	};

	constructor(private paymentsService: PaymentsService, private globalSearchService: GlobalSearchService,
		private el: ElementRef, private renderer: Renderer2) {}

	ngOnInit(): void {
		// Client-specific feature: render only where the client config opts in (same
		// pattern as paymentEntryShowSalesmanAndCustomerNotes).
		this.globalSearchService.configsubscription.subscribe((c: any) => {
			this.enabled = !!c?.remittanceImport;
		});
		this.applyDefaults();
		this.restoreScratch(this.customer);   // survive a page refresh mid-review
	}

	// A page refresh (or nav away) destroys the in-memory batch; stash the working review in
	// localStorage, keyed to the customer, so it can be restored. Mirrors the RB2 scratchpad.
	private readonly SCRATCH_KEY = 'remit_scratchpad_v1';

	ngOnChanges(changes: SimpleChanges): void {
		const c = changes['customer'];
		if (!c) { return; }
		const prev = c.previousValue, cur = c.currentValue;
		// Save the outgoing customer's review before we swap, then load the incoming one's (if any).
		if (prev?.debtorno && this.mode === 'review' && this.batch) { this.persistScratch(prev); }
		const switched = !prev || prev.debtorno !== cur?.debtorno || prev.branchcode !== cur?.branchcode;
		if (cur?.debtorno && switched) {
			this.resetInMemory();
			this.restoreScratch(cur);
		}
	}

	@HostListener('window:beforeunload')
	onBeforeUnload(): void { this.persistScratch(); }

	persistScratch(cust?: any): void {
		const c = cust || this.customer;
		if (!c?.debtorno || this.mode !== 'review' || !this.batch) { return; }
		try {
			localStorage.setItem(this.SCRATCH_KEY, JSON.stringify({
				debtorno: c.debtorno, branchcode: c.branchcode,
				batch: this.batch, header: this.header,
				postTotalOverride: this.postTotalOverride,
				filterStatus: this.filterStatus, searchText: this.searchText,
				ts: Date.now(),
			}));
		} catch (e) { /* storage unavailable/full — non-fatal */ }
	}

	private restoreScratch(cust: any): void {
		if (!cust?.debtorno) { return; }
		let s: any = null;
		try { s = JSON.parse(localStorage.getItem(this.SCRATCH_KEY) || 'null'); } catch (e) { s = null; }
		if (!s || !s.batch || s.debtorno !== cust.debtorno || (s.branchcode || '') !== (cust.branchcode || '')) { return; }
		this.batch = s.batch;
		this.header = { ...this.header, ...(s.header || {}) };
		if (this.header.datepaid) { this.header.datepaid = new Date(this.header.datepaid); }
		this.postTotalOverride = (s.postTotalOverride === undefined) ? null : s.postTotalOverride;
		this.filterStatus = s.filterStatus ?? null;
		this.searchText = s.searchText || '';
		this.mode = 'review';
		this.open = true;
	}

	private clearScratch(): void {
		try { localStorage.removeItem(this.SCRATCH_KEY); } catch (e) { /* noop */ }
	}

	// Drop the in-memory review without touching localStorage (used when switching customers).
	private resetInMemory(): void {
		this.batch = null; this.result = null; this.postTotalOverride = null;
		this.filterStatus = null; this.searchText = ''; this.mode = 'input';
	}

	private applyDefaults(): void {
		if (!this.header.bankaccount) {
			this.header.bankaccount = this.defaultBank || (this.bankaccounts?.length ? this.bankaccounts[0].accountcode : '');
		}
		if (!this.header.payment_type && this.payment_methods?.length) {
			// Prefer "Check" (how ACH remittances are booked today); else first method.
			const chk = this.payment_methods.find((m: any) => (m.paymentname || '').toLowerCase() === 'check');
			this.header.payment_type = chk ? chk.paymentid : this.payment_methods[0].paymentid;
		}
	}

	toggle(): void {
		this.open = !this.open;
		if (this.open) { this.applyDefaults(); }
		else if (this.expanded) { this.exitExpand(); }
	}

	// Focus mode. position:fixed is unreliable inside the payment modal/transformed
	// ancestors, so we teleport the host to <body> where fixed positioning is honored.
	private origParent: any = null;
	private origNext: any = null;

	toggleExpand(): void {
		this.expanded ? this.exitExpand() : this.enterExpand();
	}
	private enterExpand(): void {
		if (this.expanded) { return; }
		this.expanded = true;
		this.open = true;
		const host = this.el.nativeElement;
		this.origParent = host.parentNode;
		this.origNext = host.nextSibling;
		this.renderer.appendChild(document.body, host);
		this.renderer.addClass(document.body, 'remit-focus-lock');
	}
	private exitExpand(): void {
		if (!this.expanded) { return; }
		this.expanded = false;
		const host = this.el.nativeElement;
		if (this.origParent) {
			if (this.origNext) { this.renderer.insertBefore(this.origParent, host, this.origNext); }
			else { this.renderer.appendChild(this.origParent, host); }
		}
		this.renderer.removeClass(document.body, 'remit-focus-lock');
		this.origParent = null;
		this.origNext = null;
	}
	ngOnDestroy(): void {
		if (this.expanded) { this.renderer.removeClass(document.body, 'remit-focus-lock'); }
	}

	// Click a summary chip to show only those rows; click again to clear.
	setFilter(status: string): void {
		this.filterStatus = this.filterStatus === status ? null : status;
		this.persistScratch();
	}

	searchText = '';
	sortKey: string | null = null;
	sortDir = 1;
	private readonly ACTION = ['unmatched', 'over', 'multiple'];
	private readonly NUMERIC = ['line_no', 'paid_amount', 'matched_balance', 'alloc_amount'];

	get actionCount(): number {
		return (this.batch?.lines || []).filter((l: any) => this.ACTION.includes(l.match_status)).length;
	}
	get actionTotal(): number {
		return (this.batch?.lines || []).filter((l: any) => this.ACTION.includes(l.match_status))
			.reduce((a: number, l: any) => a + parseFloat(l.paid_amount || 0), 0);
	}

	setSort(key: string): void {
		if (this.sortKey === key) { this.sortDir = -this.sortDir; }
		else { this.sortKey = key; this.sortDir = 1; }
	}

	// Paid − invoice balance: negative = short (partial), positive = over. null when not applicable.
	lineGap(l: any): string | null {
		if (l.match_status !== 'partial' && l.match_status !== 'over') { return null; }
		if (l.matched_balance === null || l.matched_balance === '') { return null; }
		const g = Math.round((parseFloat(l.paid_amount) - parseFloat(l.matched_balance)) * 100) / 100;
		if (g < 0) { return '$' + Math.abs(g).toFixed(2) + ' short'; }
		if (g > 0) { return '$' + g.toFixed(2) + ' over'; }
		return null;
	}

	get filteredLines(): any[] {
		let out = this.batch?.lines || [];
		if (this.filterStatus === 'action') { out = out.filter((l: any) => this.ACTION.includes(l.match_status)); }
		else if (this.filterStatus) { out = out.filter((l: any) => l.match_status === this.filterStatus); }
		const q = this.searchText.trim().toLowerCase();
		if (q) {
			out = out.filter((l: any) =>
				(l.vendor_ref || '').toLowerCase().includes(q) ||
				(l.matched_transno || '').toString().toLowerCase().includes(q) ||
				(l.description || '').toLowerCase().includes(q) ||
				(l.match_note || '').toLowerCase().includes(q) ||
				(l.paid_amount || '').toString().includes(q) ||
				(l.alloc_amount || '').toString().includes(q));
		}
		if (this.sortKey) {
			const k = this.sortKey, dir = this.sortDir, num = this.NUMERIC.includes(k);
			out = out.slice().sort((a: any, b: any) => {
				const av = a[k], bv = b[k];
				const cmp = num ? (parseFloat(av || 0) - parseFloat(bv || 0))
					: (av || '').toString().localeCompare((bv || '').toString(), undefined, { numeric: true });
				return cmp * dir;
			});
		}
		return out;
	}

	// $ total behind each summary chip (null = all lines).
	statusTotal(status: string | null): number {
		const all = this.batch?.lines || [];
		const set = status ? all.filter((l: any) => l.match_status === status) : all;
		return set.reduce((a: number, l: any) => a + parseFloat(l.paid_amount || 0), 0);
	}

	dragging = false;

	onFile(event: any): void {
		this.acceptFile(event?.target?.files?.[0]);
	}

	onDrop(event: DragEvent): void {
		event.preventDefault();
		this.dragging = false;
		this.acceptFile(event.dataTransfer?.files?.[0]);
	}

	onDragOver(event: DragEvent): void {
		event.preventDefault();
		this.dragging = true;
	}

	onDragLeave(event: DragEvent): void {
		event.preventDefault();
		this.dragging = false;
	}

	private acceptFile(file: File | null | undefined): void {
		if (!file) { return; }
		const lower = file.name.toLowerCase();
		this.fileName = file.name;

		// Excel: convert the first sheet to CSV in-browser, then feed the fast CSV path (no AI).
		if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
			const reader = new FileReader();
			reader.onload = () => {
				try {
					const wb = XLSX.read(new Uint8Array(reader.result as ArrayBuffer), { type: 'array' });
					const ws = wb.Sheets[wb.SheetNames[0]];
					const csv = XLSX.utils.sheet_to_csv(ws);
					this.fileKind = 'csv';
					this.fileBase64 = 'data:text/csv;base64,' + btoa(unescape(encodeURIComponent(csv)));
				} catch (e) {
					this.clearFile();
					this.globalSearchService.showNotification('Could not read that Excel file.', 'danger', 'bottom', 'right');
				}
			};
			reader.readAsArrayBuffer(file);
			return;
		}

		const kind: 'pdf' | 'csv' | '' = lower.endsWith('.pdf') ? 'pdf'
			: (lower.endsWith('.csv') || lower.endsWith('.tsv') || lower.endsWith('.txt')) ? 'csv' : '';
		if (!kind) {
			this.fileName = '';
			this.globalSearchService.showNotification('Unsupported file — drop a PDF, Excel, or CSV.', 'warning', 'bottom', 'right');
			return;
		}
		this.fileKind = kind;
		const reader = new FileReader();
		reader.onload = () => { this.fileBase64 = String(reader.result || ''); };
		reader.readAsDataURL(file);
	}

	clearFile(): void {
		this.fileName = ''; this.fileBase64 = ''; this.fileKind = '';
	}

	canParse(): boolean {
		return !this.parsing && (this.pasteText.trim().length > 0 || (!!this.fileBase64 && !!this.fileKind));
	}

	// Honest progress copy: a PDF (and messy pasted text) needs the AI read; CSV/clean paste is instant.
	get parsingLabel(): string {
		if (this.fileKind === 'pdf') { return 'Reading the PDF…'; }
		if (this.fileKind === 'csv') { return 'Parsing the file…'; }
		return 'Reading the remittance…';
	}
	get parsingSub(): string {
		return this.fileKind === 'csv'
			? 'Matching to open invoices…'
			: 'Extracting rows, then matching to open invoices…';
	}

	parse(): void {
		if (!this.canParse()) { return; }
		const payload: any = { debtorno: this.customer?.debtorno, branchcode: this.customer?.branchcode };
		if (this.fileBase64 && this.fileKind === 'pdf') { payload.pdf = this.fileBase64; payload.source_name = this.fileName; }
		else if (this.fileBase64 && this.fileKind === 'csv') { payload.csv = this.fileBase64; payload.source_name = this.fileName; }
		else { payload.text = this.pasteText; }

		this.parsing = true;
		this.paymentsService.remitParse(payload).subscribe((res: any) => {
			this.parsing = false;
			if (res && res.success) {
				this.batch = res.batch;
				this.mode = 'review';
				this.postTotalOverride = null;   // total defaults to the allocated sum until edited
				// Open on the lines that need a decision; noise (matched/settled/credit) is one click away.
				this.filterStatus = this.actionCount > 0 ? 'action' : null;
				this.persistScratch();
			} else {
				this.globalSearchService.showNotification('Remittance parse failed: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
			}
		}, () => {
			this.parsing = false;
			this.globalSearchService.showNotification('Remittance parse failed.', 'danger', 'bottom', 'right');
		});
	}

	// Re-match / re-parse without making the operator clear + re-drop the file. If the original
	// source is still in memory (a fresh review), re-run the full parse — this also picks up any
	// server-side parser fixes. If it isn't (e.g. the review was restored after a page refresh),
	// re-run matching on the existing batch against the customer's current open invoices.
	retry(): void {
		if (this.parsing || this.posting) { return; }
		if ((this.fileBase64 && this.fileKind) || this.pasteText.trim().length > 0) {
			this.parse();
			return;
		}
		if (!this.batch?.id) { return; }
		this.parsing = true;
		this.paymentsService.remitRematch({ batch_id: this.batch.id }).subscribe((res: any) => {
			this.parsing = false;
			if (res && res.success) {
				this.batch = res.batch;
				this.postTotalOverride = null;
				this.filterStatus = this.actionCount > 0 ? 'action' : null;
				this.persistScratch();
			} else {
				this.globalSearchService.showNotification('Re-match failed: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
			}
		}, () => {
			this.parsing = false;
			this.globalSearchService.showNotification('Re-match failed.', 'danger', 'bottom', 'right');
		});
	}

	// Cash-selectable: an open-invoice match (matched/partial/over), OR an OPEN credit note the
	// customer deducted (status 'credit' with an available amount). Settled/unmatched lines never post
	// — a settled invoice is closed; any overpayment goes on account, not back onto that invoice.
	isSelectable(line: any): boolean {
		if (!line?.matched_debtortransid) { return false; }
		if (line.match_status === 'matched' || line.match_status === 'partial' || line.match_status === 'over') { return true; }
		if (line.match_status === 'credit' && parseFloat(line.alloc_amount || 0) > 0) { return true; }
		return false;
	}

	isCredit(line: any): boolean {
		return line.match_status === 'credit';
	}

	toggleInclude(line: any): void {
		if (!this.isSelectable(line)) { return; }
		line.include = line.include ? 0 : 1;
		this.persistScratch();
	}

	updateAlloc(line: any, value: any): void {
		const v = parseFloat(value);
		line.alloc_amount = isNaN(v) ? '0.00' : v.toFixed(2);
		this.persistScratch();
	}

	// Sum applied to open invoices (included invoices less applied credits).
	get allocatedTotal(): number {
		if (!this.batch?.lines) { return 0; }
		const t = this.batch.lines.reduce((acc: number, l: any) => {
			if (!l.include) { return acc; }
			const a = parseFloat(l.alloc_amount || 0);
			return this.isCredit(l) ? acc - a : acc + a;
		}, 0);
		return Math.round(t * 100) / 100;
	}

	// Editable receipt total. Defaults to the allocated sum; the operator can raise it to the full
	// cash actually received. Anything above the allocated sum is banked and left open as an
	// unapplied credit on the customer (not tied to any invoice).
	postTotalOverride: number | null = null;

	get willPostTotal(): number {
		return this.postTotalOverride !== null ? this.postTotalOverride : this.allocatedTotal;
	}

	setPostTotal(v: any): void {
		const n = parseFloat(v);
		this.postTotalOverride = (isNaN(n) || n < 0) ? null : Math.round(n * 100) / 100;
		this.persistScratch();
	}

	// Receipt cash not applied to any invoice → left open as a credit on the customer.
	get onAccountAmount(): number {
		return Math.max(0, Math.round((this.willPostTotal - this.allocatedTotal) * 100) / 100);
	}

	get includedCount(): number {
		if (!this.batch?.lines) { return 0; }
		return this.batch.lines.filter((l: any) => l.include).length;
	}

	get statedTotal(): number {
		return this.batch ? parseFloat(this.batch.recon?.stated_total || 0) : 0;
	}

	// Difference between what the remittance says and what we're about to post.
	get delta(): number {
		return Math.round((this.willPostTotal - this.statedTotal) * 100) / 100;
	}

	get selectableLines(): any[] {
		if (!this.batch?.lines) { return []; }
		return this.batch.lines.filter((l: any) => this.isSelectable(l));
	}

	get allSelected(): boolean {
		const s = this.selectableLines;
		return s.length > 0 && s.every((l: any) => l.include);
	}

	toggleAll(): void {
		const target = this.allSelected ? 0 : 1;
		this.selectableLines.forEach((l: any) => (l.include = target));
		this.persistScratch();
	}

	statusLabel(status: string): string {
		return status === 'already_settled' ? 'settled' : status;
	}

	// A line is worth inspecting when it didn't cleanly match.
	hasDetail(status: string): boolean {
		return status === 'already_settled' || status === 'unmatched' || status === 'multiple' || status === 'over' || status === 'credit';
	}

	detailLine: any = null;
	detailData: any = null;
	detailLoading = false;

	openDetail(line: any): void {
		if (!this.hasDetail(line.match_status)) { return; }
		this.detailLine = line;
		this.detailData = null;
		this.detailLoading = true;
		this.paymentsService.remitLineDetail(line.id).subscribe((res: any) => {
			this.detailLoading = false;
			this.detailData = res && res.success ? res : { error: (res?.message || 'Could not load details') };
		}, () => {
			this.detailLoading = false;
			this.detailData = { error: 'Could not load details' };
		});
	}

	closeDetail(): void {
		this.detailLine = null;
		this.detailData = null;
	}

	// ---- Validation exports (pre-post) ----
	private exportName(ext: string): string {
		const d = new Date();
		const stamp = d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
		return 'remittance-' + (this.customer?.debtorno || '') + '-' + stamp + '.' + ext;
	}

	private exportRows(): any[][] {
		return (this.batch?.lines || []).map((l: any, i: number) => ([
			i + 1,
			l.vendor_ref,
			l.description || '',
			Number(l.paid_amount),
			l.matched_transno || '',
			l.matched_balance != null ? Number(l.matched_balance) : '',
			this.statusLabel(l.match_status),
			l.include ? 'Yes' : 'No',
			l.include ? Number(l.alloc_amount) : 0,
			l.match_note || '',
		]));
	}

	private summaryPairs(): string[][] {
		const r = this.batch?.recon || {};
		const c = r.counts || {};
		return [
			['Customer', (this.customer?.debtorno || '') + ' ' + (this.customer?.name || '')],
			['Remittance total', '$' + Number(r.stated_total || 0).toFixed(2)],
			['Posting (will apply)', '$' + this.willPostTotal.toFixed(2)],
			['Difference', '$' + this.delta.toFixed(2)],
			['Lines', (this.includedCount) + ' of ' + (r.line_count || 0) + ' selected'],
			['Matched / Partial / Over', (c.matched || 0) + ' / ' + (c.partial || 0) + ' / ' + (c.over || 0)],
			['Settled / Multiple / Unmatched', (c.already_settled || 0) + ' / ' + (c.multiple || 0) + ' / ' + (c.unmatched || 0)],
		];
	}

	private readonly exportCols = ['#', 'Vendor Ref', 'Description', 'Paid', 'Invoice', 'Balance', 'Status', 'Post?', 'Allocate', 'Note'];

	exportXlsx(): void {
		if (!this.batch) { return; }
		const r = this.batch.recon || {};
		const c = r.counts || {};
		const money = '$#,##0.00';
		const cols = this.exportCols;
		const NC = cols.length;
		const ACCENT = '183D78', HEAD = '183D78', WHITE = 'FFFFFF', BAND = 'EEF3FB', BORD = 'DADDE3', INK = '1F2430', MUT = '6B7280';
		const thin = { style: 'thin', color: { rgb: BORD } };
		const box = { top: thin, bottom: thin, left: thin, right: thin };

		const rows: any[][] = [];
		rows.push(['Remittance Validation']);
		rows.push([(this.customer?.debtorno || '') + '   ' + (this.customer?.name || '')]);
		rows.push([]);
		const sum0 = rows.length;
		rows.push(['Remittance total', this.statedTotal]);
		rows.push(['Posting (will apply)', this.willPostTotal]);
		rows.push(['Difference', this.delta]);
		rows.push(['Lines selected', this.includedCount + ' of ' + (r.line_count || 0)]);
		rows.push(['Status', 'matched ' + (c.matched || 0) + ' · partial ' + (c.partial || 0) + ' · over ' + (c.over || 0) + ' · settled ' + (c.already_settled || 0) + ' · multiple ' + (c.multiple || 0) + ' · unmatched ' + (c.unmatched || 0)]);
		const sum1 = rows.length - 1;
		rows.push([]);
		const hRow = rows.length;
		rows.push(cols.slice());
		const d0 = rows.length;
		let sumPaid = 0, sumAlloc = 0;
		(this.batch.lines || []).forEach((l: any, i: number) => {
			const paid = Number(l.paid_amount) || 0;
			const alloc = l.include ? (Number(l.alloc_amount) || 0) : 0;
			sumPaid += paid; sumAlloc += alloc;
			rows.push([i + 1, l.vendor_ref, l.description || '', paid, l.matched_transno || '',
				l.matched_balance != null && l.matched_balance !== '' ? Number(l.matched_balance) : '',
				this.statusLabel(l.match_status), l.include ? 'Yes' : 'No', alloc, l.match_note || '']);
		});
		const d1 = rows.length - 1;
		rows.push(['', '', 'TOTAL', Math.round(sumPaid * 100) / 100, '', '', '', '', Math.round(sumAlloc * 100) / 100, '']);
		const tRow = rows.length - 1;

		const ws: any = XLSX.utils.aoa_to_sheet(rows);
		ws['!cols'] = [{ wch: 5 }, { wch: 12 }, { wch: 38 }, { wch: 12 }, { wch: 11 }, { wch: 12 }, { wch: 12 }, { wch: 7 }, { wch: 12 }, { wch: 36 }];
		ws['!rows'] = []; ws['!rows'][0] = { hpt: 26 }; ws['!rows'][1] = { hpt: 16 };
		ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: NC - 1 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: NC - 1 } }];
		ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: hRow, c: 0 }, e: { r: d1, c: NC - 1 } }) };

		const cell = (ri: number, ci: number): any => {
			const a = XLSX.utils.encode_cell({ r: ri, c: ci });
			if (!ws[a]) { ws[a] = { t: 's', v: '' }; }
			return ws[a];
		};
		const isMoneyCol = (ci: number) => ci === 3 || ci === 5 || ci === 8;

		cell(0, 0).s = { font: { bold: true, sz: 16, color: { rgb: WHITE } }, fill: { fgColor: { rgb: ACCENT } }, alignment: { vertical: 'center', horizontal: 'left', indent: 1 } };
		cell(1, 0).s = { font: { sz: 11, color: { rgb: MUT } }, alignment: { horizontal: 'left', indent: 1 } };

		for (let ri = sum0; ri <= sum1; ri++) {
			cell(ri, 0).s = { font: { bold: true, color: { rgb: INK } } };
			const v = cell(ri, 1);
			if (ri <= sum0 + 2) {
				const warn = (ri === sum0 + 2 && this.delta !== 0);
				v.t = 'n'; v.z = money;
				v.s = { font: { bold: ri === sum0 + 1, color: { rgb: warn ? 'C62828' : INK } }, numFmt: money };
			} else {
				v.s = { font: { color: { rgb: INK } } };
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			cell(hRow, ci).s = { font: { bold: true, color: { rgb: WHITE } }, fill: { fgColor: { rgb: HEAD } },
				alignment: { vertical: 'center', horizontal: isMoneyCol(ci) ? 'right' : (ci === 0 || ci === 7 ? 'center' : 'left') }, border: box };
		}

		for (let ri = d0; ri <= d1; ri++) {
			const band = ((ri - d0) % 2) === 1;
			for (let ci = 0; ci < NC; ci++) {
				const cc = cell(ri, ci);
				const s: any = { border: box, alignment: { vertical: 'center' } };
				if (band) { s.fill = { fgColor: { rgb: BAND } }; }
				if (isMoneyCol(ci)) {
					if (cc.v !== '') { cc.t = 'n'; cc.z = money; s.numFmt = money; }
					s.alignment.horizontal = 'right';
				}
				if (ci === 0 || ci === 7) { s.alignment.horizontal = 'center'; }
				if (ci === 2 || ci === 9) { s.alignment.wrapText = false; }
				cc.s = s;
			}
		}

		for (let ci = 0; ci < NC; ci++) {
			const cc = cell(tRow, ci);
			const s: any = { font: { bold: true, color: { rgb: INK } }, fill: { fgColor: { rgb: 'DCE6F5' } },
				border: { top: { style: 'medium', color: { rgb: HEAD } }, bottom: thin, left: thin, right: thin }, alignment: { vertical: 'center' } };
			if (ci === 3 || ci === 8) { cc.t = 'n'; cc.z = money; s.numFmt = money; s.alignment.horizontal = 'right'; }
			if (ci === 2) { s.alignment.horizontal = 'right'; }
			cc.s = s;
		}

		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Remittance');
		XLSX.writeFile(wb, this.exportName('xlsx'));
	}

	exportPdf(): void {
		if (!this.batch) { return; }
		const doc = new jsPDF({ orientation: 'landscape' });
		doc.setFontSize(14);
		doc.text('Remittance validation', 14, 14);
		doc.setFontSize(9);
		let y = 22;
		this.summaryPairs().forEach(p => { doc.text(p[0] + ':  ' + p[1], 14, y); y += 5; });
		autoTable(doc, {
			startY: y + 2,
			head: [this.exportCols],
			body: this.exportRows().map(r => r.map((v, i) => (i === 3 || i === 5 || i === 8) && v !== '' ? Number(v).toFixed(2) : v)),
			styles: { fontSize: 7, cellPadding: 1.5 },
			headStyles: { fillColor: [24, 61, 120] },
			columnStyles: { 3: { halign: 'right' }, 5: { halign: 'right' }, 8: { halign: 'right' } },
		});
		doc.save(this.exportName('pdf'));
	}

	statusClass(status: string): string {
		switch (status) {
		case 'matched': return 'st-matched';
		case 'partial': return 'st-partial';
		case 'over': return 'st-over';
		case 'multiple': return 'st-multiple';
		case 'already_settled': return 'st-settled';
		case 'credit': return 'st-credit';
		default: return 'st-unmatched';
		}
	}

	post(): void {
		if (this.posting || !this.batch) { return; }
		if (this.willPostTotal <= 0) {
			this.globalSearchService.showNotification('Nothing to post — the total is zero.', 'warning', 'bottom', 'right');
			return;
		}
		const lines = this.batch.lines.map((l: any) => ({ line_id: l.id, include: l.include ? 1 : 0, alloc_amount: l.alloc_amount }));
		const payload = {
			batch_id: this.batch.id,
			bankaccount: this.header.bankaccount,
			payment_type: this.header.payment_type,
			datepaid: this.header.datepaid,
			reference: this.header.reference,
			notes: this.header.notes,
			on_account: this.onAccountAmount,
			lines: lines,
		};
		this.posting = true;
		this.paymentsService.remitPost(payload).subscribe((res: any) => {
			this.posting = false;
			if (res && res.success) {
				this.result = res;
				this.mode = 'done';
				this.clearScratch();   // posted — the working copy is spent
				const onAcct = parseFloat(res.on_account || 0);
				const onAcctMsg = onAcct > 0 ? ' · $' + onAcct.toFixed(2) + ' on account' : '';
				this.globalSearchService.showNotification('Receipt ' + res.receipt_transno + ' posted (' + res.total + ') across ' + (res.allocated?.length || 0) + ' invoices' + onAcctMsg + '.', 'success', 'bottom', 'right');
				this.posted.emit(res);
			} else {
				this.globalSearchService.showNotification('Post failed: ' + (res?.message || 'unknown error'), 'danger', 'bottom', 'right');
			}
		}, () => {
			this.posting = false;
			this.globalSearchService.showNotification('Post failed.', 'danger', 'bottom', 'right');
		});
	}

	reset(): void {
		this.mode = 'input';
		this.pasteText = '';
		this.clearFile();
		this.batch = null;
		this.result = null;
		this.postTotalOverride = null;
		this.filterStatus = null;
		this.searchText = '';
		this.clearScratch();   // Cancel discards the working copy
	}
}
